import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Gift, Clock, QrCode, LogOut, Star, Cake, Share2, Camera, UserRound, Download } from "lucide-react";
import { QRCodeCanvas } from "qrcode.react";
import { api, errMsg, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const TYPE_LABEL = { purchase: "Purchase", welcome_bonus: "Welcome bonus", checkin: "Check-in", global_checkin: "VIP check-in", global_review: "Review reward", global_review_first: "First review bonus", global_rsvp: "RSVP", global_vote: "Awards vote", global_follow: "Follow bonus", global_profile: "Profile bonus", challenge: "Challenge bonus", tier_bonus: "Tier bonus", referral: "Referral reward", referral_bonus: "Referral bonus", birthday: "Birthday bonus", redemption: "Redemption", redemption_reserve: "Reserved", redemption_release: "Points returned", refund_reversal: "Refund reversal", refund_restore: "Points restored", manual_adjustment: "Adjustment", expiration: "Expired" };

const CARD_FALLBACK_BG = "linear-gradient(135deg,#df1721 0%,#df1721 40%,#7c0d11 75%,#1a0202 100%)";
const memberSince = (iso) => (iso ? new Date(iso).toLocaleDateString("en-US", { month: "short", year: "numeric" }) : "");

/* Digital membership card — owner-branded background (card_bg_url), optional member photo, QR of the member #.
   Exported: also used as the hero card on /vip-rewards for logged-in members. */
export function MemberCard({ program, member, userName, onChanged }) {
  const fileRef = useRef(null);
  const qrWrapRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const bgStyle = program.card_bg_url
    ? { backgroundImage: `url(${fileUrl(program.card_bg_url)})`, backgroundSize: "cover", backgroundPosition: "center" }
    : { background: CARD_FALLBACK_BG };

  const setPhoto = async (url) => {
    try {
      await api.post("/loyalty/card-photo", { program_id: program.id, photo_url: url });
      onChanged();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await setPhoto(data.url);
      toast.success("Photo added to your card");
    } catch (err) { toast.error(errMsg(err)); } finally { setBusy(false); }
  };

  const loadImg = (src) => new Promise((res, rej) => {
    const i = new Image();
    i.crossOrigin = "anonymous";
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = src;
  });

  // Renders the card to a downloadable PNG (works offline in the phone's photos / lock screen).
  const downloadCard = async () => {
    setSaving(true);
    try {
      const W = 1200, H = 760, R = 56, X = 80;
      const canvas = document.createElement("canvas");
      canvas.width = W; canvas.height = H;
      const ctx = canvas.getContext("2d");
      ctx.beginPath();
      ctx.moveTo(R, 0); ctx.arcTo(W, 0, W, H, R); ctx.arcTo(W, H, 0, H, R);
      ctx.arcTo(0, H, 0, 0, R); ctx.arcTo(0, 0, W, 0, R); ctx.closePath(); ctx.clip();
      const fallback = () => {
        const g = ctx.createLinearGradient(0, 0, W, H);
        g.addColorStop(0, "#df1721"); g.addColorStop(0.4, "#df1721");
        g.addColorStop(0.75, "#7c0d11"); g.addColorStop(1, "#1a0202");
        ctx.fillStyle = g; ctx.fillRect(0, 0, W, H);
      };
      if (program.card_bg_url) {
        try {
          const img = await loadImg(fileUrl(program.card_bg_url));
          const s = Math.max(W / img.width, H / img.height);
          ctx.drawImage(img, (W - img.width * s) / 2, (H - img.height * s) / 2, img.width * s, img.height * s);
          const ov = ctx.createLinearGradient(0, 0, W, H);
          ov.addColorStop(0, "rgba(0,0,0,0.45)"); ov.addColorStop(1, "rgba(0,0,0,0.72)");
          ctx.fillStyle = ov; ctx.fillRect(0, 0, W, H);
        } catch { fallback(); }
      } else fallback();
      // "MEMBER" corner ribbon
      ctx.fillStyle = "rgba(0,0,0,0.68)";
      ctx.beginPath();
      ctx.moveTo(W - 230, 0); ctx.lineTo(W, 0); ctx.lineTo(W, 64);
      ctx.arcTo(W - 230, 64, W - 230, 0, 28); ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#fff";
      ctx.font = "900 24px system-ui, -apple-system, sans-serif";
      ctx.textAlign = "center";
      ctx.fillText("★ MEMBER", W - 115, 42);
      ctx.textAlign = "left";
      // Overline + program name
      const listing = program.related?.entity_name ? ` · ${program.related.entity_name}` : "";
      ctx.font = "800 26px system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.9)";
      ctx.fillText(`GULF COAST VIP REWARDS${listing.toUpperCase()}`, X, 100);
      ctx.font = "900 62px system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.fillText(program.name, X, 178);
      // Optional photo
      let nameX = X;
      if (member.card_photo_url) {
        try {
          const photo = await loadImg(fileUrl(member.card_photo_url));
          const cx = X + 62, cy = 292, r = 62;
          ctx.save();
          ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2); ctx.clip();
          const s = Math.max((r * 2) / photo.width, (r * 2) / photo.height);
          ctx.drawImage(photo, cx - (photo.width * s) / 2, cy - (photo.height * s) / 2, photo.width * s, photo.height * s);
          ctx.restore();
          ctx.beginPath(); ctx.arc(cx, cy, r, 0, Math.PI * 2);
          ctx.lineWidth = 5; ctx.strokeStyle = "rgba(255,255,255,0.75)"; ctx.stroke();
          nameX = X + 150;
        } catch { /* photo optional */ }
      }
      // Member name + tier/since
      ctx.font = "700 44px system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "#fff";
      ctx.fillText(userName || "Member", nameX, 285);
      ctx.font = "500 27px system-ui, -apple-system, sans-serif";
      ctx.fillStyle = member.tier_name ? "#fcd34d" : "rgba(255,255,255,0.9)";
      const since = member.joined_at ? ` · since ${memberSince(member.joined_at)}` : "";
      ctx.fillText(`${member.tier_name ? `★ ${member.tier_name}` : "Member"}${since}`, nameX, 328);
      // Points + card number
      ctx.fillStyle = "#fff";
      ctx.font = "900 84px system-ui, -apple-system, sans-serif";
      ctx.fillText(String(member.points_available), X, 555);
      ctx.font = "600 24px system-ui, -apple-system, sans-serif";
      ctx.fillStyle = "rgba(255,255,255,0.85)";
      ctx.fillText(`${(program.points_name || "POINTS").toUpperCase()} AVAILABLE`, X, 598);
      if (member.card_number) {
        ctx.font = "600 34px ui-monospace, Menlo, monospace";
        ctx.fillStyle = "#fff";
        ctx.fillText(member.card_number, X, 672);
      }
      // QR on white plate (from the on-card QRCodeCanvas)
      const qrCanvas = qrWrapRef.current?.querySelector("canvas");
      if (qrCanvas) {
        const plate = 216, qs = 184, px = W - 64 - plate, py = H - 64 - plate, pr = 24;
        ctx.fillStyle = "#fff";
        ctx.beginPath();
        ctx.moveTo(px + pr, py); ctx.arcTo(px + plate, py, px + plate, py + plate, pr);
        ctx.arcTo(px + plate, py + plate, px, py + plate, pr); ctx.arcTo(px, py + plate, px, py, pr);
        ctx.arcTo(px, py, px + plate, py, pr); ctx.closePath(); ctx.fill();
        ctx.drawImage(qrCanvas, px + (plate - qs) / 2, py + (plate - qs) / 2, qs, qs);
      }
      const link = document.createElement("a");
      link.download = `${program.slug}-membership-card.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success("Card saved — add it to your photos or lock screen!");
    } catch (err) { toast.error("Could not render the card image"); console.error(err); } finally { setSaving(false); }
  };

  return (
    <div className="mb-5">
      <div className="vipcard relative w-full max-w-md rounded-[26px] overflow-hidden text-white shadow-2xl border border-white/10" style={bgStyle} data-testid={`member-card-${program.slug}`}>
        {program.card_bg_url && <div className="absolute inset-0" style={{ background: "linear-gradient(135deg, rgba(0,0,0,0.45), rgba(0,0,0,0.72))" }} />}
        <div className="vipcard-sheen" aria-hidden />
        <div className="absolute top-0 right-0 text-white font-black text-[10px] uppercase tracking-widest px-3 pt-2 pb-1.5 rounded-bl-2xl z-10 leading-none backdrop-blur-sm" style={{ backgroundColor: "rgba(0,0,0,0.68)" }}>
          <Star size={11} className="inline -mt-0.5 mr-1 align-middle" /> Member
        </div>
        <div className="relative p-6 pb-5">
          <p className="text-[10px] font-extrabold uppercase tracking-[0.18em] opacity-90 pr-20">
            Gulf Coast VIP Rewards{program.related?.entity_name ? ` · ${program.related.entity_name}` : ""}
          </p>
          <p className="font-display font-black text-2xl mt-1.5 leading-tight">{program.name}</p>
          <div className="mt-4 flex items-center gap-3">
            {member.card_photo_url ? (
              <img src={fileUrl(member.card_photo_url)} alt="" className="w-14 h-14 rounded-full object-cover border-2 border-white/70 shrink-0" data-testid={`member-card-photo-${program.slug}`} />
            ) : (
              <button onClick={() => fileRef.current?.click()} className="w-14 h-14 rounded-full bg-white/15 border-2 border-dashed border-white/40 flex items-center justify-center shrink-0 hover:bg-white/25 transition-colors" title="Add photo (optional)" data-testid={`member-card-photo-placeholder-${program.slug}`}>
                <UserRound size={22} className="opacity-70" />
              </button>
            )}
            <div className="min-w-0">
              <p className="font-bold text-lg leading-tight truncate">{userName}</p>
              <p className="text-xs opacity-90 mt-0.5">
                {member.tier_name ? <span className="font-bold text-amber-300">★ {member.tier_name}</span> : "Member"}
                {member.joined_at && <span> · since {memberSince(member.joined_at)}</span>}
              </p>
            </div>
          </div>
          <div className="mt-5 flex items-end justify-between gap-4">
            <div>
              <p className="font-display font-black text-3xl leading-none">{member.points_available}</p>
              <p className="text-[10px] uppercase tracking-widest opacity-80 mt-1">{program.points_name || "points"} available</p>
              {member.card_number && <p className="font-mono tracking-widest text-xs mt-3 opacity-95" data-testid={`member-card-number-${program.slug}`}>{member.card_number}</p>}
            </div>
            {member.card_number && (
              <div ref={qrWrapRef} className="bg-white rounded-xl p-1.5 shrink-0" data-testid={`member-card-qr-${program.slug}`}>
                <QRCodeCanvas value={member.card_number} size={62} bgColor="#ffffff" fgColor="#0f0f0f" level="M" />
              </div>
            )}
          </div>
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2">
        <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={upload} data-testid={`member-card-photo-input-${program.slug}`} />
        <button onClick={() => fileRef.current?.click()} disabled={busy} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1.5 font-medium" data-testid={`member-card-add-photo-${program.slug}`}>
          {busy ? <Loader2 size={12} className="animate-spin" /> : <Camera size={12} />} {member.card_photo_url ? "Change photo" : "Add photo (optional)"}
        </button>
        {member.card_photo_url && (
          <button onClick={() => setPhoto("")} className="text-xs text-zinc-500 hover:text-rose-400" data-testid={`member-card-remove-photo-${program.slug}`}>Remove photo</button>
        )}
        <button onClick={downloadCard} disabled={saving} className="text-xs text-zinc-400 hover:text-white flex items-center gap-1.5 font-medium" data-testid={`member-card-download-${program.slug}`}>
          {saving ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Save card
        </button>
      </div>
    </div>
  );
}

export function MyRewardsPanel() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const [redeemed, setRedeemed] = useState(null);
  const [bday, setBday] = useState("");

  const load = () => api.get("/my/rewards").then((r) => setData(r.data)).catch(() => setData({ programs: [] }));
  useEffect(() => { load(); }, []);

  // Pre-fill the check-in code when arriving from a scanned QR poster
  // (URL like /my-rewards?checkin=ABC123). We don't auto-submit — the user
  // still taps "Check in" so they can confirm before points are awarded.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const fromQr = params.get("checkin");
    if (fromQr) {
      setCode(fromQr);
      params.delete("checkin");
      const clean = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (clean ? `?${clean}` : ""));
    }
  }, []);

  if (!data) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-rose-500" /></div>;

  const saveBirthday = async () => {
    try {
      await api.post("/loyalty/birthday", { birthday: bday, consent: true });
      toast.success("Birthday saved — bonus points will arrive on your big day!");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const shareReferral = async (program) => {
    try {
      const { data: d } = await api.get(`/loyalty/referral-code?program_id=${program.id}`);
      const url = `${window.location.origin}${d.path}`;
      try { await navigator.clipboard.writeText(url); } catch (e) { console.warn("Clipboard write blocked", e); }
      toast.success(`Invite link copied! Earn ${d.referral_points} ${d.points_name} per friend's first purchase.`, { description: url });
    } catch (e) { toast.error(errMsg(e)); }
  };

  const checkin = async () => {
    try {
      const { data: d } = await api.post("/loyalty/checkin", { code });
      toast.success(`+${d.points} ${d.points_name} at ${d.program_name}!`);
      if (d.vip_bonus) {
        toast.success(`+${d.vip_bonus.points} VIP Points — Gulf Coast VIP Rewards check-in bonus!`);
        (d.vip_bonus.challenge_bonus || []).forEach((c) => toast.success(`Challenge complete: ${c.name} +${c.points} VIP Points!`));
      }
      setCode(""); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const redeem = async (reward) => {
    try {
      const { data: d } = await api.post("/loyalty/redeem", { reward_id: reward.id });
      setRedeemed(d); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const leave = async (p) => {
    try { await api.post("/loyalty/leave", { program_id: p.id }); toast.success("Left program"); load(); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="my-rewards-panel">
      <style>{`.vipcard-sheen{position:absolute;inset:-50%;background:linear-gradient(115deg,transparent 40%,rgba(255,255,255,0.22) 50%,transparent 60%);transform:translateX(-40%);animation:vipSheen 5s ease-in-out infinite;pointer-events:none;}@keyframes vipSheen{0%,100%{transform:translateX(-40%);}50%{transform:translateX(40%);}}`}</style>
      <div className="bg-[#111] border border-white/10 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3">
        <p className="text-sm font-bold flex items-center gap-2 shrink-0"><QrCode size={16} className="text-rose-400" /> Loyalty check-in</p>
        <div className="flex gap-2 flex-1">
          <input value={code} onChange={(e) => setCode(e.target.value)} placeholder="Enter check-in code"
            className="w-full bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-rose-500 text-sm" data-testid="rewards-checkin-code" />
          <button onClick={checkin} disabled={!code.trim()} className="shrink-0 bg-rose-500 hover:bg-rose-600 disabled:opacity-40 px-4 rounded-lg text-sm font-bold" data-testid="rewards-checkin-btn">Check in</button>
        </div>
      </div>

      {data.birthday_rewards_enabled && !data.birthday && data.programs.length > 0 && (
        <div className="bg-gradient-to-r from-amber-500/10 to-rose-500/5 border border-amber-500/30 rounded-2xl p-4 mb-6 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="rewards-birthday-card">
          <p className="text-sm font-bold flex items-center gap-2 shrink-0"><Cake size={16} className="text-amber-300" /> Add your birthday for bonus points</p>
          <div className="flex gap-2 flex-1">
            <input value={bday} onChange={(e) => setBday(e.target.value)} placeholder="MM-DD (e.g. 07-04)"
              className="w-full sm:w-44 bg-[#0a0a0a] border border-white/10 rounded-lg px-3 py-2 outline-none focus:border-amber-500 text-sm" data-testid="rewards-birthday-input" />
            <button onClick={saveBirthday} disabled={!bday.trim()} className="shrink-0 bg-amber-500 hover:bg-amber-600 text-black disabled:opacity-40 px-4 rounded-lg text-sm font-bold" data-testid="rewards-birthday-save">Save</button>
          </div>
          <p className="text-[11px] text-zinc-500 shrink-0">By saving you consent to birthday reward messages.</p>
        </div>
      )}

      {data.programs.length === 0 && (
        <div className="py-16 text-center text-zinc-500">
          <Gift className="mx-auto mb-3 text-zinc-700" size={44} />
          You haven't joined any rewards programs yet. Look for "Join rewards" on your favorite artists, venues and attractions!
        </div>
      )}

      <div className="space-y-6">
        {data.programs.map(({ member, program, rewards, recent, expiring_soon }) => (
          <div key={program.id} className="bg-[#111] border border-white/10 rounded-2xl p-6" data-testid={`rewards-program-${program.slug}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div className="flex items-center gap-3">
                {program.image_url && <img src={fileUrl(program.image_url)} alt="" className="w-12 h-12 rounded-xl object-cover" />}
                <div>
                  <p className="font-display font-bold text-lg flex items-center gap-2">{program.name}
                    {program.is_global && <span className="bg-rose-500/15 text-rose-400 border border-rose-500/40 text-[10px] font-black tracking-wide px-2 py-0.5 rounded-full" data-testid="rewards-global-badge">PLATFORM-WIDE</span>}
                  </p>
                  <p className="text-xs text-zinc-500">{program.is_global ? <a href="/vip-rewards" className="text-rose-400 hover:text-rose-300 font-bold">Program details, tiers & catalog →</a> : (program.related?.entity_name || "")}{member.tier_name && <span className="ml-2 text-amber-300 font-bold">★ {member.tier_name}</span>}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                {data.referrals_enabled && program.referral_enabled && (
                  <button onClick={() => shareReferral(program)} className="text-xs text-amber-300 hover:text-amber-200 flex items-center gap-1 font-bold" data-testid={`rewards-refer-${program.slug}`}><Share2 size={12} /> Invite friends</button>
                )}
                <button onClick={() => leave(program)} className="text-xs text-zinc-500 hover:text-rose-400 flex items-center gap-1" data-testid={`rewards-leave-${program.slug}`}><LogOut size={12} /> Leave</button>
              </div>
            </div>
            <MemberCard program={program} member={member} userName={user?.name || ""} onChanged={load} />
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
              <div className="bg-[#0a0a0a] rounded-xl p-3"><p className="text-[11px] text-zinc-500">Available</p><p className="font-display font-extrabold text-xl text-emerald-300" data-testid={`rewards-available-${program.slug}`}>{member.points_available}</p><p className="text-[10px] text-zinc-500">{program.points_name || "points"}</p></div>
              <div className="bg-[#0a0a0a] rounded-xl p-3"><p className="text-[11px] text-zinc-500">Pending</p><p className="font-display font-extrabold text-xl text-amber-300">{member.points_pending}</p></div>
              <div className="bg-[#0a0a0a] rounded-xl p-3"><p className="text-[11px] text-zinc-500">Lifetime earned</p><p className="font-display font-extrabold text-xl">{member.lifetime_earned}</p></div>
              <div className="bg-[#0a0a0a] rounded-xl p-3"><p className="text-[11px] text-zinc-500">Redeemed</p><p className="font-display font-extrabold text-xl">{member.lifetime_redeemed}</p></div>
            </div>
            {expiring_soon > 0 && <p className="text-xs text-amber-300 flex items-center gap-1.5 mb-3"><Clock size={13} /> {expiring_soon} {program.points_name || "points"} expiring soon — use them before they're gone!</p>}

            {rewards.length > 0 && (
              <div className="mb-4">
                <p className="text-sm font-bold mb-2 flex items-center gap-2"><Gift size={14} className="text-rose-400" /> Rewards</p>
                <div className="grid sm:grid-cols-2 gap-2">
                  {rewards.map((r) => {
                    const afford = member.points_available >= r.points_cost;
                    return (
                      <div key={r.id} className="flex items-center justify-between gap-3 bg-[#0a0a0a] border border-white/5 rounded-xl px-4 py-3">
                        <div><p className="text-sm font-bold">{r.name}</p><p className="text-xs text-zinc-500">{r.points_cost} {program.points_name || "points"}</p></div>
                        <button onClick={() => redeem(r)} disabled={!afford} data-testid={`rewards-redeem-${r.id}`}
                          className="shrink-0 text-xs font-bold px-3 py-1.5 rounded-full bg-rose-500 hover:bg-rose-600 disabled:opacity-30">Redeem</button>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {recent.length > 0 && (
              <details className="text-sm">
                <summary className="cursor-pointer text-zinc-400 text-xs font-medium">Recent activity</summary>
                <div className="mt-2 space-y-1.5">
                  {recent.map((e) => (
                    <div key={e.id} className="flex justify-between text-xs text-zinc-400">
                      <span>{TYPE_LABEL[e.type] || e.type} — {e.note}</span>
                      <span className={e.points >= 0 ? "text-emerald-300 font-bold" : "text-rose-300 font-bold"}>{e.points >= 0 ? "+" : ""}{e.points}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        ))}
      </div>

      <Dialog open={!!redeemed} onOpenChange={(v) => !v && setRedeemed(null)}>
        <DialogContent className="bg-[#111] border-white/10 text-center">
          <DialogHeader><DialogTitle className="text-center">Reward redeemed!</DialogTitle></DialogHeader>
          <Star className="mx-auto text-amber-300" size={36} />
          <p className="font-bold">{redeemed?.reward_name}</p>
          <p className="text-sm text-zinc-400">Show this one-time code to the seller:</p>
          <p className="font-mono text-2xl text-rose-400 tracking-widest" data-testid="rewards-redemption-code">{redeemed?.code}</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function MyRewards() {
  const { user, loading } = useAuth();
  const nav = useNavigate();
  useEffect(() => { if (!loading && !user) nav("/login"); }, [user, loading, nav]);
  if (!user) return <div className="py-40 text-center text-zinc-400">Loading...</div>;
  return (
    <div className="px-6 md:px-12 lg:px-20 py-12 min-h-screen" data-testid="my-rewards-page">
      <p className="overline text-rose-400">Gulf Coast VIP Rewards</p>
      <h1 className="font-display font-extrabold text-3xl md:text-4xl mb-8">My rewards</h1>
      <MyRewardsPanel />
    </div>
  );
}
